import {
  ConsentCategory,
  ConsentDocument,
  ConsentSignerRole,
  ConsentTemplateInput,
  ConsentTemplateInputType,
} from "@source/api/models/consentModels";
import {ConsentStepsValues, isConsentPdfHeader, normalizeConsentIds} from "@source/api/models/consentHelpers";

export interface SignerPerson {
  _id: string;
  firstName: string;
  lastName: string;
  secondaryLabel?: string;
}

export type PersistStage = "saving" | "uploading" | "confirming" | "sending" | null;

export const persistStageText: Record<Exclude<PersistStage, null>, string> = {
  saving: "Saving draft",
  uploading: "Uploading PDF",
  confirming: "Confirming PDF",
  sending: "Sending",
};

const selectableSignerRoles = [
  ConsentSignerRole.EMPLOYEE,
  ConsentSignerRole.FAMILY_MEMBER,
  ConsentSignerRole.GP_CLINICAL,
];

/** Resident-only categories have no one for staff to pick, so the signer step is omitted. */
export const categoryNeedsSignerSelection = (
  category: {requiredSignerRoles?: ConsentSignerRole[]} | null | undefined,
) => Boolean(category?.requiredSignerRoles?.some((role) => selectableSignerRoles.includes(role)));

export const consentStepCount = (category: {requiredSignerRoles?: ConsentSignerRole[]} | null | undefined) =>
  categoryNeedsSignerSelection(category) ? 3 : 2;

export const consentStepSubtitle = (step: number) => {
  if (step === 1) return "Select Consent Category";
  if (step === 2) return "Review the document";
  return "Select who should sign";
};

export const signerName = (person: {firstName?: string; lastName?: string}) =>
  `${person.firstName || ""} ${person.lastName || ""}`.trim();

export const signerLabel = (person: SignerPerson) =>
  [signerName(person), person.secondaryLabel].filter(Boolean).join(" · ");

const contactTypeLabel = (value?: string) =>
  value ? `${value.charAt(0).toUpperCase()}${value.slice(1)} contact` : "";

export const uniqueSignerPeople = (people: SignerPerson[]) =>
  people.filter((person, index) => people.findIndex((candidate) => candidate._id === person._id) === index);

export const activeFamilySigners = (resident: {familyRelations?: any[]}): SignerPerson[] =>
  uniqueSignerPeople((resident?.familyRelations || [])
    .filter((relation) => relation.status === "active" && relation.familyMember?._id && !relation.familyMember.isDisabled)
    .map((relation) => ({
      ...relation.familyMember,
      secondaryLabel: [contactTypeLabel(relation.contactType), relation.relationship].filter(Boolean).join(" · "),
    })));

export const rolesText = (category: Pick<ConsentCategory, "requiredSignerRoles" | "signingOrderRequired" | "signingOrder">) => {
  const names: Record<ConsentSignerRole, string> = {
    [ConsentSignerRole.EMPLOYEE]: "Employees",
    [ConsentSignerRole.GP_CLINICAL]: "GP / Clinician",
    [ConsentSignerRole.FAMILY_MEMBER]: "Family members",
    [ConsentSignerRole.RESIDENT]: "Resident",
  };
  const order = category.signingOrder.filter((role) => category.requiredSignerRoles.includes(role));
  const roles = order.length ? order : category.requiredSignerRoles;
  return category.signingOrderRequired
    ? `Role order: ${roles.map((role) => names[role]).join(" → ")}`
    : "Roles may sign in any order";
};

export const actionableConsentError = (error: unknown, fallback: string) => {
  const status = typeof error === "object" && error !== null && "status" in error ? (error as {status?: number}).status : undefined;
  if (status === 400) return "Some selected people or fields are no longer valid. Review the form and try again.";
  if (status === 403) return "You do not have permission for this nursing home or consent document.";
  if (status === 404) return "This consent resource is no longer available. Return to consent history and try again.";
  if (status === 409) return "This draft changed or is no longer editable. Refresh the document to see its current state.";
  return error instanceof Error ? error.message : fallback;
};

export const readableFileSize = (bytes: number) =>
  `${(bytes / (1024 * 1024)).toFixed(bytes < 1024 * 1024 ? 2 : 1)} MiB`;

export const fileHasPdfHeader = async (file: File) => {
  const header = await file.slice(0, 5).arrayBuffer();
  return isConsentPdfHeader(new Uint8Array(header));
};

export const valuesFromDraft = (draft: ConsentDocument): ConsentStepsValues => ({
  categoryId: draft.categoryId || draft.categorySnapshot?.categoryId || "",
  employeeSignerIds: normalizeConsentIds(draft.requiredEmployeeSigners.map((person) => person._id)),
  familyMemberIds: normalizeConsentIds(
    (draft.familyMembers?.length ? draft.familyMembers : draft.familyMember ? [draft.familyMember] : [])
      .map((person) => person._id),
  ),
  familyFieldCompleterId: draft.familyFieldCompleter?._id || "",
  employeeInputValues: draft.employeeInputValues || {},
  clinicalSigningMode: draft.clinicalSigningMode || "",
  clinicalSignerId: draft.clinicalSigner?._id || "",
});

export const incompleteStaffInputs = (
  inputs: ConsentTemplateInput[],
  values: ConsentStepsValues["employeeInputValues"],
) => inputs.filter((input) => {
  const value = values[input.id];
  if (input.type === ConsentTemplateInputType.TEXT) return !(typeof value === "string" && value.trim());
  if (input.type === ConsentTemplateInputType.RADIO) return typeof value !== "string" || !value;
  return false;
});
